import { makeStyles } from "tss-react/mui";
import { MiBadge } from "@miplatform/components";
import {
  DATE_TIME_FORMATS,
  FONT_WEIGHTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { toFormattedDate } from "@miplatform/utils";
import { MiPrintTableContent } from "@mibuildweb/hooks";
import { Box, Typography, useTheme } from "@mui/material";
import { MATERIAL_SOURCES } from "./materialConstants";
import MaterialImage from "./components/MaterialImage";

const getMaterialSourceColorMap = (theme) => ({
  [MATERIAL_SOURCES.ORDER]: {
    backgroundColor: theme.palette.orange.main,
    borderColor: theme.palette.deepOrange.dark,
  },
  [MATERIAL_SOURCES.RESTOCK]: {
    backgroundColor: theme.palette.blue.main,
    borderColor: theme.palette.blue.dark,
  },
});

export const getMaterialSourceColor = (source, theme) => {
  const colorMap = getMaterialSourceColorMap(theme);
  return colorMap[source] || colorMap[MATERIAL_SOURCES.RESTOCK];
};

const MetadataField = ({ label, value }) => {
  const { classes } = useStyles();

  return (
    <Box minWidth={0}>
      <Typography className={classes.metadataLabel}>{label}</Typography>
      <Typography className={classes.metadataValue}>{value || "--"}</Typography>
    </Box>
  );
};

const getAddressValue = (address) => {
  const { addressLine1, addressLine2, city, regionCode, postalCode } =
    address || {};

  return (
    <>
      {addressLine1 || "--"}
      {addressLine2 && (
        <>
          <br />
          {addressLine2}
        </>
      )}
      {city && (
        <>
          <br />
          {`${city}, ${regionCode || ""} ${postalCode || ""}`.trim()}
        </>
      )}
    </>
  );
};

export const CloseoutMaterialMetadata = ({
  address,
  communityName,
  dispatcherName,
  technicianName,
  performedOn,
  className,
  showJobDetails = false,
}) => {
  const { classes, cx } = useStyles({ showJobDetails });

  return (
    <Box className={cx(classes.metadata, className)}>
      {showJobDetails && (
        <>
          <MetadataField label="Address" value={getAddressValue(address)} />
          <MetadataField label="Community" value={communityName} />
          <MetadataField label="Dispatcher" value={dispatcherName} />
        </>
      )}
      <MetadataField label="Technician" value={technicianName} />
      <MetadataField
        label="Performed On"
        value={
          performedOn &&
          toFormattedDate(performedOn, DATE_TIME_FORMATS.FORMAT_MMMM_D_YYYY)
        }
      />
    </Box>
  );
};

export const MaterialBadge = ({ text, backgroundColor, borderColor }) => {
  const { classes } = useStyles();
  const theme = useTheme();

  return (
    <MiBadge
      text={text || "--"}
      backgroundColor={backgroundColor}
      color={theme.palette.common.white}
      borderColor={borderColor}
      className={classes.materialBadge}
    />
  );
};

const getPrintColumns = ({ showSource, theme }) => {
  const columns = [
    {
      label: "Image",
      field: "documents",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => <MaterialImage item={row} />,
    },
    {
      label: "Brand",
      field: "brand",
      width: 120,
    },
    {
      label: "Description",
      field: "description",
      width: showSource ? 460 : 560,
      minWidth: 280,
      renderCell: ({ value, row }) => (
        <>
          <Typography
            fontSize="inherit"
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {value || "--"}
          </Typography>
          {row.requiresReplacement && (
            <Box
              style={{
                marginLeft: theme.spacing(1),
                background: theme.palette.blue.main,
                borderRadius: theme.spacing(1),
                padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
                color: theme.palette.common.white,
              }}
            >
              <Typography
                fontSize="inherit"
                style={{
                  fontSize: "10px",
                }}
              >
                RESTOCK
              </Typography>
            </Box>
          )}
        </>
      ),
    },
    {
      label: "Quantity",
      field: "quantity",
      width: 90,
    },
  ];

  if (!showSource) {
    return columns;
  }

  return [
    ...columns,
    {
      label: "Source",
      field: "printSource",
      width: 100,
      renderCell: ({ value }) => {
        if (
          value !== MATERIAL_SOURCES.ORDER &&
          value !== MATERIAL_SOURCES.RESTOCK
        ) {
          return value || "--";
        }

        return (
          <MaterialBadge
            text={value}
            {...getMaterialSourceColor(value, theme)}
          />
        );
      },
    },
  ];
};

const toPrintRow = (material) => ({
  ...material,
  printSource:
    material.source === "Stock" && material.requiresReplacement
      ? MATERIAL_SOURCES.RESTOCK
      : material.source,
});

const CloseoutMaterialPrintContent = ({
  address,
  communityName,
  dispatcherName,
  materialSections,
  technicianName,
  performedOn,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  return (
    <Box display="flex" flexDirection="column" gap={2} mb={2}>
      <Typography
        variant={TYPOGRAPHY_VARIANTS.H5}
        fontWeight={FONT_WEIGHTS.SEMI_BOLD}
      >
        Materials List
      </Typography>
      <CloseoutMaterialMetadata
        address={address}
        communityName={communityName}
        dispatcherName={dispatcherName}
        technicianName={technicianName}
        performedOn={performedOn}
        showJobDetails
      />
      {materialSections.map(({ key, title, materials, showSource = true }) =>
        materials.length ? (
          <Box key={key} display="flex" flexDirection="column" gap={1}>
            <Typography className={classes.sectionTitle}>{title}</Typography>
            <MiPrintTableContent
              data={materials.map(toPrintRow)}
              columns={getPrintColumns({ showSource, theme })}
              gridProps={{
                autoHeight: true,
                rowHeight: 72,
              }}
            />
          </Box>
        ) : null
      )}
    </Box>
  );
};

export default CloseoutMaterialPrintContent;

const useStyles = makeStyles()((theme, { showJobDetails = false } = {}) => ({
  metadata: {
    display: showJobDetails ? "grid" : "flex",
    ...(showJobDetails
      ? {
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          columnGap: theme.spacing(3),
        }
      : { gap: theme.spacing(8) }),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
  },
  metadataLabel: {
    color: theme.palette.grey.medium,
    marginBottom: theme.spacing(0.5),
    fontSize: 11,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  metadataValue: {
    fontSize: 12,
    fontWeight: 600,
  },
  materialBadge: {
    minWidth: 80,
    height: 30,
    boxSizing: "border-box",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(3),
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 400,
    borderRadius: 2,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: "uppercase",
  },
}));
